import React, { Suspense, lazy } from 'react'
import { render } from 'react-dom'
import { BrowserRouter as Router, Switch, Route, Redirect, Link, NavLink } from 'react-router-dom'
import { Provider } from 'react-redux'
import store from './store'
import SuspenseLoader from './components/suspense_loader/SuspenseLoader'
import GlobalNav from '@digitalmedia/sn-global-nav'
import GlobalFooter from '@digitalmedia/global-footer'
import ScrollToTop from './navigation/ScrollToTop'
import RouteListener from './navigation/RouteListener'
import { getParameterByName } from './utils/js/browser_helpers'

import './utils/scss/global.scss'

const Standings = lazy(() => import('./containers/standings/Standings'))
const Players = lazy(() => import('./containers/players/Players'))
const Search = lazy(() => import('./containers/search/Search'))
const Stats = lazy(() => import('./containers/stats/Stats'))
const Videos = lazy(() => import('./containers/videos/Videos'))
const Livetrackers = lazy(() => import('./containers/livetrackers/Livetrackers'))
const Scoreboard = lazy(() => import('./containers/scoreboard/Scoreboard'))
const Team = lazy(() => import('./containers/team/Team'))
const PlayerProfile = lazy(() => import('./containers/player_profile/PlayerProfile'))
const AncillarySport = lazy(() => import('./containers/ancillary_sport/AncillarySport'))
const AncillaryLeague = lazy(() => import('./containers/ancillary_league/AncillaryLeague'))
const HockeyTournament = lazy(() => import('./containers/tournaments/hockey/HockeyTournament'))
const SoccerTournament = lazy(() => import('./containers/tournaments/soccer/SoccerTournament'))
const Schedule = lazy(() => import('./containers/schedule/Schedule'))
const PlayoffTracker = lazy(() => import('./containers/playoff_tracker/PlayoffTracker'))
const NotFoundGeneral = lazy(() => import('./components/NotFoundGeneral'))
const TradeSigningsTracker = lazy(() =>
    import('./containers/trade_signings_tracker/TradeSigningsTracker')
)
const MedalTracker = lazy(() => import('./containers/medal_tracker/MedalTracker'))
const Gamecode = lazy(() => import('./containers/gamecode/Gamecode'))
const BasketballTournament = lazy(() =>
    import('./containers/tournaments/basketball/BasketballTournament')
)
const LacrosseTournament = lazy(() =>
    import('./containers/tournaments/lacrosse/LacrosseTournament')
)

const Nav = () => {
    const isBasicTemplate = getParameterByName('template') === 'basic'
    const navWithProps = {
        link: Link,
        navlink: NavLink,
    }
    return !isBasicTemplate && <GlobalNav withNavProps={navWithProps} />
}

const Footer = () => {
    const isBasicTemplate = getParameterByName('template') === 'basic'
    return !isBasicTemplate && <GlobalFooter />
}

const router = (
    <Provider store={store}>
        <Router>
            <ScrollToTop>
                <RouteListener>
                    <Route path="/:sport?/:league?" component={Nav} />
                    <Suspense fallback={<SuspenseLoader />}>
                        <Switch>
                            <Route exact path="/:sport/:league/stats" component={Stats} />
                            <Route exact path="/:sport/:league/players" component={Players} />
                            <Route exact path="/:sport/:league/standings" component={Standings} />
                            <Route
                                exact
                                path="/:sport/:league/playoffs"
                                component={PlayoffTracker}
                            />
                            <Route
                                exact
                                path="/:sport/:league/:page(trade-tracker|signings)"
                                component={TradeSigningsTracker}
                            />
                            <Route
                                exact
                                path="/:sport/:league/games/:gamecode"
                                component={Livetrackers}
                            />
                            <Route
                                exact
                                path="/:sport/:league/players/:player_name/:player_id"
                                component={PlayerProfile}
                            />

                            <Redirect
                                exact
                                from="/:sport(golf|ufc|tennis|auto-racing)/:league/"
                                to="/:sport(golf|ufc|tennis|auto-racing)/:league/schedule"
                            />
                            <Route
                                exact
                                path="/:sport(golf|ufc|tennis|auto-racing)/:league/:page(schedule|results|rankings|events)/:id?"
                                component={AncillaryLeague}
                            />

                            <Route
                                exact
                                path="/:sport/:league/scores/:day?"
                                component={Scoreboard}
                            />
                            <Route
                                exact
                                path="/:sport/:league/teams/:teamName/:teampage?"
                                component={Team}
                            />
                            <Route
                                exact
                                path="/videos/:sport?/:league?/:slug?"
                                component={Videos}
                            />
                            <Route
                                exact
                                path="/soccer/euro/:day?"
                                component={props => (
                                    <SoccerTournament sport="soccer" league="euro" {...props} />
                                )}
                            />
                            <Route
                                exact
                                path="/olympics/basketball/:page/:year?"
                                component={props => (
                                    <BasketballTournament
                                        sport="basketball"
                                        league="oly"
                                        {...props}
                                    />
                                )}
                            />
                            <Redirect
                                exact
                                from="/olympics/basketball"
                                to="/olympics/basketball/womens/2020"
                            />
                            <Route
                                exact
                                path="/olympics/hockey/:page/:year?"
                                component={props => (
                                    <HockeyTournament sport="hockey" league="oly" {...props} />
                                )}
                            />
                            <Redirect
                                exact
                                from="/olympics/hockey"
                                to="/olympics/hockey/womens/2022"
                            />
                            <Route
                                exact
                                path="/olympics/medal-table/:year"
                                component={props => <MedalTracker league="oly" {...props} />}
                            />
                            <Redirect
                                exact
                                from="/olympics/medal-table"
                                to="/olympics/medal-table/2022"
                            />
                            <Route
                                exact
                                path="/olympics/soccer/:page/:year?"
                                component={props => (
                                    <SoccerTournament sport="soccer" league="oly" {...props} />
                                )}
                            />
                            <Redirect
                                exact
                                from="/olympics/soccer"
                                to="/olympics/soccer/womens/2020"
                            />
                            <Route
                                exact
                                path="/soccer/:leagueOrSubPage/:dayOrSubPage?"
                                component={props => <AncillarySport sport="soccer" {...props} />}
                            />
                            <Route
                                exact
                                path="/juniors/world-juniors/:day?"
                                component={props => (
                                    <HockeyTournament sport="juniors" league="wjc" {...props} />
                                )}
                            />
                            <Route
                                exact
                                path="/hockey/wwhc/:day?"
                                component={props => (
                                    <HockeyTournament sport="hockey" league="wwhc" {...props} />
                                )}
                            />
                            <Route
                                exact
                                path="/hockey/whc/:day?"
                                component={props => (
                                    <HockeyTournament sport="hockey" league="whc" {...props} />
                                )}
                            />
                            <Route
                                exact
                                path="/juniors/:leagueOrSubPage/:dayOrSubPage?"
                                component={props => <AncillarySport sport="juniors" {...props} />}
                            />
                            <Route
                                exact
                                path="/basketball/wnba/:day?"
                                component={props => (
                                    <BasketballTournament
                                        sport="basketball"
                                        league="wnba"
                                        {...props}
                                    />
                                )}
                            />
                            <Route
                                exact
                                path="/lacrosse/nll/:day?"
                                component={props => (
                                    <LacrosseTournament sport="lacrosse" league="nll" {...props} />
                                )}
                            />
                            <Route exact path="/schedule/:day?" component={Schedule} />
                            <Route exact path="/search/:keyword?" component={Search} />
                            <Route exact path="/gamecode" component={Gamecode} />
                            <Route component={NotFoundGeneral} />
                        </Switch>
                    </Suspense>
                    <Route path="/:sport?/:league?" component={Footer} />
                </RouteListener>
            </ScrollToTop>
        </Router>
    </Provider>
)

render(router, document.getElementById('root'))
